import { Account, AccountState } from 'core';
import {
  renderColumn,
  ColumnDefinition,
  SortDescriptor,
  sortableColumn,
  getCheckedColumn
} from 'components/TableColumn/TableColumn';
import styles from './accountList.module.scss';
import {
  nameFormatter,
  statusFormatter,
  nameHeaderFormatter
} from './accountListFormatters';
import { AccountListColumns } from './AccountListColumnSetting';
import _ from 'lodash';
import { Pagination } from 'core/pagination/Pagination';

export interface AccountListModel {
  getColumnDefinition(columnName: string): ColumnDefinition;

  readonly accounts: Array<Account>;
  readonly noDataDescription: string;
  readonly defaultSorts: SortDescriptor;
  readonly columns: any[];
  readonly selectedAccounts: number[];
  readonly rootPath: string;
  readonly pagination?: Pagination;
  readonly onTableChange?: (type: string, props: any) => void;
  readonly handleOnSelect?: (account: Account) => void;
  readonly handleOnSelectAll?: () => void;
  readonly handleRemoveSelect?: () => void;
  readonly activateAccounts?: (event: any) => Promise<void>;
  readonly deactivateAccounts?: (event: any) => Promise<void>;
}

export type AccountListProps = {
  readonly model: AccountListModel;
};

export class DefaultAccountListModel implements AccountListModel {
  accounts: any[];
  selectedAccounts: number[];
  pagination?: Pagination;
  onTableChange?: (type: string, props: any) => void;
  handleOnSelect?: (account: Account) => void;
  handleOnSelectAll?: () => void;
  handleRemoveSelect?: () => void;
  activateAccounts?: (event: any) => Promise<void>;
  deactivateAccounts?: (event: any) => Promise<void>;

  constructor(
    accounts: Array<Account>,
    protected isSysAdmin: boolean,
    selectedAccounts: number[] = [],
    pagination?: Pagination,
    onTableChange?: (type: string, props: any) => void,
    handleOnSelect?: (account: Account) => void,
    handleOnSelectAll?: () => void,
    handleRemoveSelect?: () => void,
    activateAccounts?: (event: any) => Promise<void>,
    deactivateAccounts?: (event: any) => Promise<void>
  ) {
    this.accounts = accounts.map((account) => ({
      ...account,
      pending: account.status === AccountState.PENDING
    }));
    this.selectedAccounts = selectedAccounts;
    this.pagination = pagination;
    this.onTableChange = onTableChange;
    this.handleOnSelect = handleOnSelect;
    this.handleOnSelectAll = handleOnSelectAll;
    this.handleRemoveSelect = handleRemoveSelect;
    this.activateAccounts = activateAccounts;
    this.deactivateAccounts = deactivateAccounts;
  }

  get noDataDescription(): string {
    return 'accounts.placeholder.noDataAvailable';
  }

  getColumnDefinition(columnName: string): ColumnDefinition {
    const columnClassGetter = () => {
      return styles[columnName];
    };
    return {
      ...sortableColumn(columnName, `accounts.headers.${columnName}`),
      classes: columnClassGetter,
      headerClasses: columnClassGetter
    };
  }

  get rootPath(): string {
    return '/accounts';
  }

  getNameColumn() {
    const columnDef = this.getColumnDefinition(AccountListColumns.NAME);
    const formatter = nameFormatter.bind(
      null,
      this.rootPath,
      this.handleOnSelect,
      this.selectedAccounts
    );
    if (this.handleOnSelect && this.handleOnSelectAll) {
      const headerFormatter = nameHeaderFormatter.bind(
        null,
        this.handleOnSelectAll,
        this.selectedAccounts.length,
        this.accounts.length
      );
      return {
        ...renderColumn(columnDef, formatter),
        headerFormatter
      };
    }
    return renderColumn(columnDef, formatter);
  }

  get columns() {
    return _.compact([
      this.getNameColumn(),
      renderColumn(this.getColumnDefinition(AccountListColumns.EMAIL)),
      renderColumn(
        this.getColumnDefinition(AccountListColumns.STATUS),
        statusFormatter
      ),
      this.isSysAdmin
        ? getCheckedColumn(
            this.getColumnDefinition(AccountListColumns.IS_ADMIN)
          )
        : undefined,
      getCheckedColumn(this.getColumnDefinition(AccountListColumns.PENDING))
    ]);
  }

  get defaultSorts(): SortDescriptor {
    return [
      {
        dataField: AccountListColumns.ID,
        order: 'desc'
      }
    ];
  }
}
export class OEMAccountListModel implements AccountListModel {
  accounts: Array<Account>;
  selectedAccounts: number[];
  pagination?: Pagination;
  onTableChange?: (type: string, props: any) => void;
  handleOnSelect?: (account: Account) => void;
  handleOnSelectAll?: () => void;
  handleRemoveSelect?: () => void;
  activateAccounts?: (event: any) => Promise<void>;
  deactivateAccounts?: (event: any) => Promise<void>;

  constructor(
    accounts: Array<Account>,
    selectedAccounts: number[] = [],
    pagination?: Pagination,
    onTableChange?: (type: string, props: any) => void,
    handleOnSelect?: (account: Account) => void,
    handleOnSelectAll?: () => void,
    handleRemoveSelect?: () => void,
    activateAccounts?: (event: any) => Promise<void>,
    deactivateAccounts?: (event: any) => Promise<void>
  ) {
    this.accounts = accounts;
    this.selectedAccounts = selectedAccounts;
    this.pagination = pagination;
    this.onTableChange = onTableChange;
    this.handleOnSelect = handleOnSelect;
    this.handleOnSelectAll = handleOnSelectAll;
    this.handleRemoveSelect = handleRemoveSelect;
    this.activateAccounts = activateAccounts;
    this.deactivateAccounts = deactivateAccounts;
  }

  get noDataDescription(): string {
    return 'accounts.placeholder.noDataAvailable';
  }

  getColumnDefinition(columnName: string): ColumnDefinition {
    const columnClassGetter = () => {
      return styles[columnName];
    };
    return {
      ...sortableColumn(columnName, `accounts.headers.${columnName}`),
      classes: columnClassGetter,
      headerClasses: columnClassGetter
    };
  }

  get rootPath(): string {
    return '/oem-accounts';
  }

  getNameColumn() {
    const columnDef = this.getColumnDefinition(AccountListColumns.NAME);
    const formatter = nameFormatter.bind(
      null,
      this.rootPath,
      this.handleOnSelect,
      this.selectedAccounts
    );
    if (this.handleOnSelect && this.handleOnSelectAll) {
      const headerFormatter = nameHeaderFormatter.bind(
        null,
        this.handleOnSelectAll,
        this.selectedAccounts.length,
        this.accounts.length
      );
      return {
        ...renderColumn(columnDef, formatter),
        headerFormatter
      };
    }
    return renderColumn(columnDef, formatter);
  }

  get columns() {
    return [
      this.getNameColumn(),
      renderColumn(this.getColumnDefinition(AccountListColumns.EMAIL)),
      renderColumn(this.getColumnDefinition(AccountListColumns.COMPANY_NAME)),
      renderColumn(
        this.getColumnDefinition(AccountListColumns.STATUS),
        statusFormatter
      )
    ];
  }

  get defaultSorts(): SortDescriptor {
    return [
      {
        dataField: AccountListColumns.ID,
        order: 'desc'
      }
    ];
  }
}
